import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/upr-api";
import { ArrowLeft, X } from "lucide-react";
import DOMPurify from "dompurify";

// Shared sanitizer config for editor-generated HTML (bio, description, credits).
const RICH_TEXT_SANITIZE = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "a", "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "hr"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
  // NOTE: KEEP_CONTENT defaults to true — do NOT set it to false. Setting
  // false makes DOMPurify strip the inner text of allowed tags like <p>.
};

export default function ArtistDetail() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [releases, setReleases] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = (searchParams.get("tag") || "").toLowerCase();

  useEffect(() => {
    Promise.all([
      api.get(`/artists/${id}`).then(({ data }) => setArtist(data)),
      api.get(`/artists/${id}/releases`).then(({ data }) => setReleases(data)),
    ]).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="artist-not-found">
        <Link to="/artists" className="upr-link-underline text-sm">← Back to Artists</Link>
        <h1 className="font-heading font-black text-5xl mt-8">Artist not found.</h1>
      </div>
    );
  }
  if (!artist) return <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="artist-loading">Loading…</div>;

  // Tag cloud built from THIS artist's releases (sorted by frequency)
  const tagCounts = (() => {
    const counts = {};
    releases.forEach((r) => (r.tags || []).forEach((t) => {
      const k = String(t).trim();
      if (!k) return;
      counts[k] = (counts[k] || 0) + 1;
    }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  })();

  const filtered = activeTag
    ? releases.filter((r) => (r.tags || []).some((t) => String(t).toLowerCase() === activeTag))
    : releases;

  const clearTag = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("tag");
    setSearchParams(sp, { replace: true });
  };

  const setTag = (t) => {
    setSearchParams({ tag: t }, { replace: false });
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16" data-testid="artist-detail">
      <Link to="/artists" className="inline-flex items-center gap-1 upr-link-underline text-sm" data-testid="back-to-artists">
        <ArrowLeft size={14} /> Back to Artists
      </Link>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12 mt-8">
        <div className="md:col-span-5">
          <div className="upr-cover aspect-square" style={{ border: "1px solid var(--upr-border)" }}>
            <img src={artist.photo_url} alt={artist.name} className="w-full h-full object-cover" data-testid="artist-photo" />
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="upr-eyebrow mb-3">Artist</div>
          <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl" style={{ letterSpacing: "-0.035em" }} data-testid="artist-name">
            {artist.name}
          </h1>
          {artist.bio && (
            <div
              className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl upr-prose"
              data-testid="artist-bio"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(artist.bio, RICH_TEXT_SANITIZE) }}
            />
          )}

          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            {artist.website && <a className="upr-link-underline" target="_blank" rel="noreferrer" href={artist.website}>Website</a>}
            {artist.bandcamp && <a className="upr-link-underline" target="_blank" rel="noreferrer" href={artist.bandcamp}>Bandcamp</a>}
            {artist.instagram && <a className="upr-link-underline" target="_blank" rel="noreferrer" href={artist.instagram}>Instagram</a>}
          </div>
        </div>
      </div>

      {/* TAG CLOUD (only when this artist has any tagged releases) */}
      {tagCounts.length > 0 && (
        <section className="mt-16 md:mt-20" data-testid="artist-tag-cloud-section">
          <div className="upr-eyebrow mb-4">Browse {artist.name}&apos;s catalogue by tag</div>
          <div className="flex flex-wrap gap-2" data-testid="artist-tag-cloud">
            {tagCounts.map(([t, n]) => {
              const isActive = activeTag === t.toLowerCase();
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => (isActive ? clearTag() : setTag(t))}
                  data-testid={`artist-tag-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 font-heading text-xs transition-colors uppercase"
                  style={{
                    background: isActive ? "var(--upr-fg)" : "var(--upr-surface)",
                    color: isActive ? "var(--upr-bg)" : "var(--upr-fg)",
                    border: "1px solid " + (isActive ? "var(--upr-fg)" : "var(--upr-border)"),
                    letterSpacing: "0.04em",
                  }}
                >
                  {t}
                  <span className="upr-eyebrow" style={{ fontSize: "0.55rem", color: isActive ? "var(--upr-bg)" : "var(--upr-muted)" }}>{n}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="mt-12 md:mt-16" data-testid="discography">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter" style={{ letterSpacing: "-0.03em" }}>
            {activeTag ? <>Tagged <span className="uppercase" style={{ color: "var(--upr-primary)" }}>#{activeTag}</span></> : "Discography"}
          </h2>
          <div className="flex items-center gap-3">
            <div className="upr-eyebrow">{filtered.length} {filtered.length === 1 ? "release" : "releases"}</div>
            {activeTag && (
              <button
                type="button"
                onClick={clearTag}
                data-testid="artist-clear-tag"
                className="upr-eyebrow inline-flex items-center gap-1 hover:text-[color:var(--upr-primary)]"
              >
                <X size={12} /> Clear filter
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--upr-muted)" }}>
            {activeTag ? `No releases by ${artist.name} are tagged with “${activeTag}”.` : "No releases yet for this artist."}
          </div>
        ) : (
          <ul className="border-t" style={{ borderColor: "var(--upr-border)" }}>
            {filtered.map((r) => (
              <li key={r.id} className="border-b" style={{ borderColor: "var(--upr-border)" }}>
                <Link to={`/release/${r.id}`} className="flex items-center gap-5 py-4 group" data-testid={`disc-${r.id}`}>
                  <div className="upr-cover w-16 h-16 shrink-0" style={{ border: "1px solid var(--upr-border)" }}>
                    <img src={r.cover_url} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-lg leading-tight truncate" style={{ letterSpacing: "-0.01em" }}>{r.title}</div>
                    <div className="text-sm truncate" style={{ color: "var(--upr-muted)" }}>{r.catalog_number} · {r.format} · {r.year}</div>
                    {r.tags && r.tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.tags.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="px-1.5 py-0.5 font-heading uppercase"
                            style={{
                              fontSize: "0.55rem",
                              letterSpacing: "0.06em",
                              background: "var(--upr-surface)",
                              border: "1px solid var(--upr-border)",
                              color: "var(--upr-muted)",
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="upr-eyebrow hidden sm:block">View →</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
